import { useState } from "react";
import "@/App.css";
import { ChevronDown, ChevronUp } from "lucide-react";

// Expandable Section Component
const ExpandableSection = ({ title, shortContent, longContent, id }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section
      data-testid={`section-${id}`}
      className="glass-panel p-8 md:p-12 mb-8 fade-in-up"
    >
      <h2 className="text-2xl md:text-3xl font-light tracking-wide text-white/90 mb-6">
        {title}
      </h2>
      <div className="text-base md:text-lg text-slate-300 leading-relaxed font-light">
        <p>{shortContent}</p>
        
        <div
          className={`expandable-content ${isExpanded ? 'expanded' : ''}`}
        >
          {longContent}
        </div>
        
        {longContent && (
          <button
            data-testid={`learn-more-${id}`}
            onClick={() => setIsExpanded(!isExpanded)}
            className="learn-more-btn mt-6 inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-300"
          >
            {isExpanded ? (
              <>
                Show Less <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                Learn More <ChevronDown className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>
    </section>
  );
};

// Simple Section Component (no expand)
const SimpleSection = ({ title, content, id }) => {
  return (
    <section
      data-testid={`section-${id}`}
      className="glass-panel p-8 md:p-12 mb-8 fade-in-up"
    >
      <h2 className="text-2xl md:text-3xl font-light tracking-wide text-white/90 mb-6">
        {title}
      </h2>
      <p className="text-base md:text-lg text-slate-300 leading-relaxed font-light">
        {content}
      </p>
    </section>
  );
};

function App() {
  return (
    <div className="App min-h-screen bg-[#090D0F]">
      {/* Fixed Logo - styled like reference site */}
      <div 
        data-testid="logo"
        className="fixed top-4 left-4 md:top-6 md:left-8 z-50 logo-container"
      >
        <img 
          src="https://customer-assets.emergentagent.com/job_shen-about/artifacts/3lxi8j0v_2_3-removebg-preview.png"
          alt="Ask Shen Logo"
          className="h-10 md:h-14 w-auto logo-visible"
        />
      </div>

      {/* Hero Section - 2 column layout like reference */}
      <section data-testid="hero-section" className="min-h-screen flex items-center justify-center relative overflow-hidden pt-24 pb-12">
        {/* Background decorative elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-20 w-96 h-96 rounded-full blur-3xl" style={{background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)'}}></div>
          <div className="absolute bottom-40 left-20 w-96 h-96 rounded-full blur-3xl" style={{background: 'radial-gradient(circle, rgba(0, 0, 0, 0.1) 0%, transparent 70%)'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left side - Text content */}
            <div className="space-y-6 md:space-y-8 fade-in-up order-1">
              <h1 className="text-white font-bold text-4xl sm:text-5xl md:text-6xl leading-tight">
                Meet Shen
              </h1>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed">
                Relationship therapist, Researcher, and Founder of the Viruthi Centre for Flourishing Families.
              </p>
              
              {/* Journey Milestones - Desktop (stacked) */}
              <div className="hidden md:flex flex-col gap-3 pt-4">
                <div className="milestone-item-desktop">
                  <span className="text-3xl font-semibold text-white">7+</span>
                  <span className="text-base text-white/60 ml-3">Years of Experience</span>
                </div>
                <div className="milestone-item-desktop">
                  <span className="text-3xl font-semibold text-white">1000+</span>
                  <span className="text-base text-white/60 ml-3">Clients Served</span>
                </div>
                <div className="milestone-item-desktop">
                  <span className="text-3xl font-semibold text-white">20+</span>
                  <span className="text-base text-white/60 ml-3">Countries</span>
                </div>
              </div>
            </div>
            
            {/* Right side - Image in glass panel */}
            <div className="flex justify-center fade-in-up order-2" style={{animationDelay: '0.2s'}}>
              <div className="glass-panel p-4 hover-lift">
                <img 
                  src="https://customer-assets.emergentagent.com/job_3b4aa1e9-f202-45f1-b2f4-4146af8379fc/artifacts/fpn0tz79_04fedb8a-fff9-4c18-84ee-be8010894e9f%20copy.JPG"
                  alt="Shenbakam Natarajan"
                  className="rounded-2xl w-full max-w-md object-cover shadow-2xl"
                  style={{height: '500px'}}
                />
              </div>
            </div>
            
            {/* Journey Milestones - Mobile only (stacked below image) */}
            <div className="md:hidden order-3 col-span-full">
              <div className="flex flex-col gap-4 pt-6">
                <div className="milestone-item-mobile">
                  <span className="text-3xl font-semibold text-white">7+</span>
                  <span className="text-base text-white/60 ml-3">Years of Experience</span>
                </div>
                <div className="milestone-item-mobile">
                  <span className="text-3xl font-semibold text-white">1000+</span>
                  <span className="text-base text-white/60 ml-3">Clients Served</span>
                </div>
                <div className="milestone-item-mobile">
                  <span className="text-3xl font-semibold text-white">20+</span>
                  <span className="text-base text-white/60 ml-3">Countries</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-20">
        
        {/* Meet Shen Section */}
        <ExpandableSection
          id="meet-shen"
          title="Meet Shen"
          shortContent="I am a relationship therapist, researcher, and the founder of the Viruthi Centre for Flourishing Families. For the past seven years, I have worked with thousands of individuals and couples across 20 countries, helping them heal emotional wounds, rebuild trust, and create deeply fulfilling relationships."
          longContent={
            <div className="mt-4 space-y-4">
              <p>
                My work focuses on understanding the emotional patterns that silently shape relationships — the conflicts, disconnection, and pain that couples often struggle to express. I am trained in Emotionally Focused Couple Therapy Interventions and specialize in relationship distress, trauma, and emotional healing. I am also a Certified Clinical Trauma Professional (CCTP-1) and a Sex Therapy Informed Professional (CSTIP).
              </p>
              <p>
                Academically, I hold a Master's degree in Medical and Psychiatric Social Work from the Madras School of Social Work, and I am UGC JRF/NET qualified. I am currently pursuing my doctoral research at CHRIST University, where my work explores emotional bonds and marital flourishing in intimate relationships. I have also contributed book chapters on the books, emotional wellbeing, intergenerational bonding, and collective flourishing in India-Published by Springer Nature.
              </p>
              <p>
                Before entering the field of relationship therapy, In my previous Avatar, I worked as a Senior Information Security Engineer at Infosys. I chose to leave the corporate world to follow a deeper calling, helping people build healthier relationships and stronger families.
              </p>
            </div>
          }
        />

        {/* My Story Section */}
        <ExpandableSection
          id="my-story"
          title="My Story"
          shortContent="I always knew I wanted to work with people, but for a long time I didn't know how. During my college years and the years that followed, I immersed myself deeply in the world of self-help and personal development. For nearly seven years, I was reading books, blogs, and constantly exploring what makes people grow, heal, and live meaningful lives."
          longContent={
            <div className="mt-4 space-y-4">
              <p>Then something happened that shifted my direction.</p>
              <p>
                A close friend of mine was going through a divorce. Around the same time, another friend & colleague was also going through a divorce. Watching people I cared about struggle made me ask a deeper question,
              </p>
              <p className="italic text-white/70">
                "Why are people struggling so much in something that is supposed to be a source of love and joy?"
              </p>
              <p>
                Relationships are meant to give us belonging, safety, and companionship. Yet for many people, they become a source of pain and confusion.
              </p>
              <p>
                Around me, I always had friends who were older people who had been married for 10–15 years and had children. Conversations with them often revolved around marriage and family life. I found those conversations endlessly fascinating.
              </p>
              <p className="text-white/80 font-medium">I mean, world revolves around love!</p>
              <p>That curiosity slowly became a calling.</p>
              <p>
                I decided that I wanted to work with families. To pursue this path, I enrolled for my Master's in Medical and Psychiatric Social Work at the Madras School of Social Work, known for its intensive fieldwork.
              </p>
              <p>During those two years, I interned with several organizations.</p>
              <p>
                I Interned at Indian Council for Child Welfare, Tamil Nadu - Childline, where the kind of cases that came in deeply shook me.
              </p>
              <p>
                Later, I worked with I Love Mondays Pvt Ltd, an organisation which was helping preteens and teens with career counselling based on Howard Gardner's Multiple Intelligences theory. This exposed me to another side of child development, understanding strengths, talents, and potential.
              </p>
              <p>
                I also interned at the National Institute of Epidemiology (ICMR), researching gaming addiction among private school children at a time when PUBG was at its peak.
              </p>
              <p>
                I later trained at NIMHANS, Bangalore, gaining exposure to mental health practice and research.
              </p>
              <p>
                Much of my work during those years was with children, and I was also conducting few personality development workshops for kids.
              </p>
              <p className="text-white/80 font-medium">But slowly, a realization emerged.</p>
              <p className="text-white/80">Children are deeply affected by the emotional health of their parents.</p>
              <p>
                When parents are struggling, disconnected, or unhappy in their relationship, children inevitably carry that emotional burden.
              </p>
              <p className="text-white font-medium text-lg">
                That is when I realized something that changed my direction completely,
              </p>
              <p className="text-white/90 italic text-lg">
                If I truly want to help children, I must help their parents.
              </p>
              <p>
                Couples enter marriage with very little preparation. There is no manual, no structured education about relationships. People often enter marriage with assumptions and expectations, and when reality feels different, they struggle silently.
              </p>
              <p className="text-white/80 font-medium">
                Many couples don't flourish in marriage. They simply endure it.
              </p>
              <p>This realization shaped my life's work.</p>
              <div className="mt-6 p-6 bg-white/5 rounded-xl">
                <p className="text-white/90">I believe that happy couples create happy families.</p>
                <p className="text-white/90">Happy families raise happy children.</p>
                <p className="text-white/90">Happy children grow into emotionally healthy adults.</p>
                <p className="text-white/90">And emotionally healthy adults build flourishing communities.</p>
              </div>
              <p className="mt-4">This belief drives my mission.</p>
              <p className="text-white/90 font-medium">
                India has nearly 35 crore households. My mission is to help at least 10 crore families build healthier, happier relationships, because when families flourish, the entire nation flourishes.
              </p>
            </div>
          }
        />

        {/* My Philosophy Section */}
        <SimpleSection
          id="my-philosophy"
          title="My Philosophy"
          content="Connection thrives where understanding begins. I believe in creating safe spaces where vulnerability is honored and every emotion is valid. True healing happens when we stop trying to fix each other and start trying to understand each other."
        />

        {/* My Approach Section */}
        <SimpleSection
          id="my-approach"
          title="My Approach"
          content="In relationships, logic rarely heals disconnection. Emotions do. My work uses an emotion-focused, trauma-informed approach to help couples understand each other's deeper emotional needs and rebuild secure bonds."
        />

      </main>
    </div>
  );
}

export default App;
